import { defineStore } from "pinia";
import { fetchMyOrganisations, type OrganisationChoice } from "@/organisation/organisationApi";
import type { HostOrganisation } from "@/state/globalState";

/**
 * 🔴 **WHICH COMPANY YOU ARE ACTING FOR** — the shell's third piece of context, beside the session and
 * the production (D37, D41).
 *
 * Metaship's shape, brought in as-is (D42). Its switcher sits at the top of the sidebar; choosing one
 * is a **client-side change of a header sent on every subsequent request** — `config.headers.apikey =
 * store.state.orgToken` there, `X-Organisation` here. There is no re-authentication and no new token:
 * **identity is stable, the organisation is a choice.** That is the whole design, and it is why this
 * store can be plain state with no round trip on a switch.
 *
 * 🔴 **THE HEADER IS WHAT THE HUMAN SELECTED, and it wins.** The gate records the incident behind that
 * rule (Metaship's FIN-564): a desynced credential "used to scope SILENTLY to the JWT's org — reads
 * and writes landed in an organisation the user was not looking at". So the choice lives here, on the
 * screen, and travels as a header — never as a claim baked into a token that can go stale while the
 * person keeps working.
 *
 * Three facts, and they arrive separately:
 *   choices  from `GET /me/organisations`, once a session is live. The switcher's list.
 *   chosen   the person's selection, remembered for the tab. Sent as `X-Organisation`.
 *   ready    false until the first answer lands, so the sidebar draws a placeholder rather than
 *            flashing "no organisations" at somebody who has three.
 */
interface OrganisationContextState {
  choices: OrganisationChoice[];
  /** The public id of the chosen organisation, or null before a choice is possible. */
  chosenId: string | null;
  loading: boolean;
  /** True once a list has been answered, however empty. */
  ready: boolean;
}

/**
 * 🔴 Remembered per TAB, not per browser (`sessionStorage`).
 *
 * Two tabs open on two companies is a real way to work — a producer comparing one slate against
 * another — and `localStorage` would make the second tab silently move the first. The same reasoning
 * the session token is held under, for the same reason: the choice belongs to the screen it was made
 * on. It is not a secret and it is not authorisation; the gate re-checks membership and standing on
 * every single request, so a stale or tampered value buys nothing but a refusal.
 */
export const CHOSEN_ORGANISATION_KEY = "pinksy:organisation";

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** How the store reaches the API. Injected whole in tests; the real call by default. */
export interface OrganisationContextDeps {
  fetchAll: () => Promise<OrganisationChoice[]>;
}

const defaultDeps: OrganisationContextDeps = { fetchAll: () => fetchMyOrganisations() };

let deps: OrganisationContextDeps = defaultDeps;

/** Test seam — `stores/__tests__/organisation.test.ts` swaps the API call out. */
export function setOrganisationContextDeps(overrides: Partial<OrganisationContextDeps> | null): void {
  deps = overrides === null ? defaultDeps : { ...defaultDeps, ...overrides };
}

/**
 * 🔴 The COMPANY row for an id, never the agency row (D164). A person with a staff edge AND a represented_talent
 * edge to the same agency gets two rows with the same id, in no fixed order (the list sorts on legalName, which
 * ties). Resolving by id alone could land on the represented row — `selectable: false`, so the switch did nothing,
 * and `relationship: "represented_talent"` went out to every module as the host organisation. The staff edge wins.
 */
function companyRow(choices: ReadonlyArray<OrganisationChoice>, id: string | null): OrganisationChoice | null {
  if (id === null) return null;
  return choices.find((choice) => choice.id === id && !choice.represents) ?? null;
}

export const useOrganisationStore = defineStore("shell-organisation", {
  state: (): OrganisationContextState => ({
    choices: [],
    chosenId: null,
    loading: false,
    ready: false,
  }),
  getters: {
    /** Only the ones a person may actually enter. A suspended company is listed, never chosen. */
    selectable(state): OrganisationChoice[] {
      return state.choices.filter((choice) => choice.selectable);
    },
    /**
     * 🔴 What the SWITCHER lists: the companies this person works at, suspended ones included (greyed).
     * An agency that represents them (D164) is not one of those — drawn here it would read as a company they
     * had been locked out of. It stays in `choices`, which is the whole answer.
     */
    companies(state): OrganisationChoice[] {
      return state.choices.filter((choice) => !choice.represents);
    },
    /** The agencies that represent this person (D164), any number of them — drawn, never chosen. */
    agencies(state): OrganisationChoice[] {
      return state.choices.filter((choice) => choice.represents);
    },
    /** The chosen row in full, or null. */
    chosen(state): OrganisationChoice | null {
      return companyRow(state.choices, state.chosenId);
    },
    /**
     * 🔴 Whether to offer a CHANGE at all.
     *
     * One selectable organisation is not a choice, and a control that opens onto a list of one is
     * ceremony a person has to dismiss. The name is still drawn — it is context — but nothing invites
     * a switch that cannot go anywhere.
     */
    canSwitch(): boolean {
      return (this as unknown as { selectable: OrganisationChoice[] }).selectable.length > 1;
    },
    /** What goes on the host state, so a module reads the organisation the way it reads the user. */
    hostOrganisation(state): HostOrganisation | null {
      const chosen = companyRow(state.choices, state.chosenId);
      if (chosen === null) return null;
      return { id: chosen.id, name: chosen.name, relationship: chosen.relationship, functions: chosen.functions };
    },
  },
  actions: {
    /**
     * The list, once a session is live. Idempotent and self-guarding, so a caller may ask on every
     * navigation: it costs one request per session and nothing thereafter.
     */
    async load(): Promise<void> {
      if (this.loading) return;
      this.loading = true;
      /**
       * 🔴 `finally`, because `loading` is also the RE-ENTRY guard on the line above. A rejected
       * list left it true forever, so every later call returned at that guard and the tab could
       * never ask again — and now that `modulesMayMount` waits on `ready` (D48), a single failed
       * `GET /me/organisations` would mean a Desk that never mounts a module rather than one that
       * mounts a refused one. The next thing to set a session status asks again instead.
       */
      try {
        const choices = await deps.fetchAll();
        this.choices = choices;
        this.ready = true;
        this.settleChoice();
        this.publish();
      } finally {
        this.loading = false;
      }
    },

    /**
     * 🔴 Which organisation the session lands in, in priority order, and why each step exists.
     *
     *   1. The one this tab was already on, IF it is still selectable. A person who reloads is in the
     *      same place they were.
     *   2. Otherwise the first selectable one, alphabetically — the list's own order. A session that
     *      names none is a session where every scoped endpoint refuses, so landing somewhere is
     *      strictly better than landing nowhere.
     *   3. Otherwise nothing, which is honest: a person who belongs to no live company has no
     *      organisation to act in, and the screens say so rather than pretending.
     *
     * 🔴 A remembered id that is no longer selectable is DROPPED here rather than sent and refused —
     * the standing may have changed while the tab was open. Dropping it is not a security decision
     * (the gate makes that on every request); it is so the sidebar names where the person actually
     * is.
     */
    settleChoice(): void {
      const remembered = this.chosenId ?? storage()?.getItem(CHOSEN_ORGANISATION_KEY) ?? null;
      const stillGood = companyRow(this.choices, remembered)?.selectable === true;
      const next = stillGood ? remembered : (this.choices.find((choice) => choice.selectable && !choice.represents)?.id ?? null);
      if (next !== this.chosenId) {
        this.chosenId = next;
        this.remember(next);
      }
    },

    /**
     * The person chose one. A no-op for an unknown or unselectable id: the switcher does not offer
     * those, so being asked for one is a bug somewhere else, and obeying it would put the whole
     * session into a context every request then refuses.
     */
    choose(organisationId: string): boolean {
      const choice = companyRow(this.choices, organisationId);
      if (choice === null || !choice.selectable) return false;
      if (choice.id === this.chosenId) return true;
      this.chosenId = choice.id;
      this.remember(choice.id);
      this.publish();
      return true;
    },

    /** Forget everything on sign-out: the next person on this tab starts from their own list. */
    clear(): void {
      this.choices = [];
      this.chosenId = null;
      this.ready = false;
      this.remember(null);
      this.publish();
    },

    /**
     * On `window.PinksyGlobalState`, beside the user and the production — so a module reads which
     * company it is drawing for the same way it reads everything else, and hears about a switch
     * through the subscription it already has.
     */
    publish(): void {
      window.PinksyGlobalState?.updateState({ organisation: this.hostOrganisation, organisationSettled: this.ready });
    },

    remember(organisationId: string | null): void {
      try {
        if (organisationId === null) storage()?.removeItem(CHOSEN_ORGANISATION_KEY);
        else storage()?.setItem(CHOSEN_ORGANISATION_KEY, organisationId);
      } catch {
        // best-effort — the choice then lasts as long as this page does, which is still a working session
      }
    },
  },
});

/**
 * 🔴 The chosen organisation, readable WITHOUT Pinia.
 *
 * `auth/http.ts` attaches the header on every request, including ones made before any component has
 * mounted and from code that is not in a Vue context at all. Reaching for the store there would make
 * the HTTP layer depend on the UI layer; reading the same tab-scoped value the store writes keeps the
 * dependency pointing the one way it should.
 */
export function chosenOrganisationId(): string | null {
  try {
    return storage()?.getItem(CHOSEN_ORGANISATION_KEY) ?? null;
  } catch {
    return null;
  }
}
